<template>
    <div class="footer-margin">
        <div class="directory">
            <Breadcrumb />
            <Search :showCalendar="true" :title="'Kulturkalender'" :selectedDate="dataSet.query.selectedDate"
                :selectedTimeFrame="dataSet.query.selectedTimeFrame" :selectedCategory="dataSet.query.selectedCategory"
                :selectedTag="dataSet.query.selectedTag" @dateTimeChanged="dateTimeSearchParamsHaveChanged"
                @reload="searchParamsHaveChanged" />


            <div class="container-fluid" v-if="dataSet.total > 0">
                <div class="row mt30">
                    <div class="col-xl-8 offset-xl-1 col-lg-10 col-md-12">
                        <div class="row directory--element" v-for="group in categoryContentList" :key="group.category">
                            <div class="col-12">
                                <h3 class="title">{{ group.category }}</h3>
                            </div>

                            <div class="col-lg-4 col-md-6 directory--element__single" v-for="content in group.contents"
                                :key="content.id" :id="'event-' + content.id" :data-event-id="content.id"
                                @click.capture="rememberLastVisited(content.id)">
                                <CardEvent :content="content" :date="dataSet.query.selectedDate" :marginRight="'0px'"
                                    @click.native="rememberLastVisited(content.id)" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div v-else class="no-calendar-results">
                <div class="container-fluid blank">
                    <div class="blank-section">
                        <img src="/assets/blank-image.png" alt="Blank">
                        <h4 class="mt20">Keine Termine für diese Auswahl gefunden.</h4>
                    </div>
                </div>
            </div>
        </div>

    </div>

</template>

<script>
import Breadcrumb from '@/components/controls/Breadcrumb.vue';
import Search from '@/components/Search.vue';
import CardEvent from '@/components/cards/CardEvent.vue';

import { showLoader, hideLoader, sortByProperty, getFieldValues, buildFilterSignatureFromQuery, saveLastVisitedItem, loadLastVisitedItem, filterEventsByDate } from '@/utils/helpers';
import { getContentsOfType } from '@/api/content';


export default {
    name: 'CultureCalendar',
    components: {
        Breadcrumb,
        Search,
        CardEvent
    },
    data() {
        return {
            loader: null,
            contents: null,
            dataSet: {
                total: 0,
                data: null,
                meta: null,
                query: {
                    page: 1,
                    limit: 50,
                    keyword: '',
                    type: 'view',
                    selectedDate: new Date(),
                    selectedTimeFrame: null,
                    selectedCategory: null,
                    selectedTag: null,
                    selectedAppId: this.$appId,
                    view_status: 1,
                    selectedContentTypeId: 2,
                    sort: {
                        prop: '',
                        order: 'asc'
                    },
                },
            },
        }
    },
    computed: {
        categoryContentList() {
            if (!this.contents || !Array.isArray(this.contents)) {
                return [];
            }

            const resultSet = [];
            let currentCategory = null;
            let currentResultSet = null;

            for (const content of this.contents) {
                // Skip items without category
                if (!content.category) continue;

                // If category changes or first iteration
                if (content.category !== currentCategory) {
                    // Push previous result set if exists
                    if (currentResultSet) {
                        resultSet.push(currentResultSet);
                    }

                    // Create new result set
                    currentCategory = content.category;
                    currentResultSet = {
                        category: content.category,
                        contents: []
                    };
                }

                // Add content to current category group
                currentResultSet.contents.push(content);
            }

            // Push the last result set
            if (currentResultSet) {
                resultSet.push(currentResultSet);
            }

            return resultSet;
        }
    },
    created() {
        this.initializeFromUrl();
    },
    methods: {
        showLoader,
        hideLoader,
        sortByProperty,
        async getContents() {
            this.loader = this.showLoader(this.loader);
            const { limit, page } = this.dataSet.query;

            // Filter out past events - only show events from selected date or today onwards
            // Use local time to avoid timezone issues
            const today = new Date();
            const todayLocal = new Date(today.getFullYear(), today.getMonth(), today.getDate());

            // Use selected date if provided, otherwise use today
            let startDate = todayLocal;
            if (this.dataSet.query.selectedDate) {
                const selectedDate = this.dataSet.query.selectedDate instanceof Date
                    ? this.dataSet.query.selectedDate
                    : new Date(this.dataSet.query.selectedDate);
                // Create a local date object (avoid UTC conversion issues)
                const selectedDateLocal = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate());
                // Use the later of selected date or today (don't show past events)
                startDate = selectedDateLocal >= todayLocal ? selectedDateLocal : todayLocal;
            }

            // Ensure selectedDate is always a Date object (for component props)
            this.dataSet.query.selectedDate = startDate;

            // Format date as YYYY-MM-DD for API call (create a copy of query to avoid modifying selectedDate)
            const queryForApi = { ...this.dataSet.query };
            const year = startDate.getFullYear();
            const month = String(startDate.getMonth() + 1).padStart(2, '0');
            const day = String(startDate.getDate()).padStart(2, '0');
            queryForApi.selectedDate = `${year}-${month}-${day}`;

            // Sort by date ascending
            queryForApi.sort = JSON.stringify({
                prop: 'date',
                order: 'asc'
            });

            const { data, meta } = await getContentsOfType(this.$offerId, queryForApi);

            // Frontend filtering workaround: filter out past events
            const filteredData = filterEventsByDate(data, startDate);

            this.dataSet.data = filteredData;
            this.dataSet.data.forEach((element, index) => {
                element['index'] = (page - 1) * limit + index + 1;
            });
            // Update total to reflect filtered count
            this.dataSet.meta = meta;
            this.dataSet.total = filteredData.length;
            this.enhanceContentAttribsWithCategory();
            this.contents = this.sortContentsByCategories();
            this.loader = this.hideLoader(this.loader);

            // After contents are loaded, attempt to scroll to the last visited item for the current filter signature
            this.$nextTick(() => {
                try {
                    const signature = this.getCurrentFilterSignature();
                    const lastId = loadLastVisitedItem(this.$route.path, signature);
                    if (lastId && typeof lastId === 'number') {
                        // Try a few times in case of lazy rendering/components
                        let attempts = 0;
                        const tryScroll = () => {
                            attempts++;
                            const elem = document.getElementById(`event-${lastId}`) || document.querySelector(`[data-event-id="${lastId}"]`);
                            if (elem) {
                                elem.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            } else if (attempts < 10) {
                                setTimeout(tryScroll, 100);
                            }
                        };
                        tryScroll();
                    }
                } catch (e) { /* no-op */ }
            });
        },
        getCurrentFilterSignature() {
            // Build a stable signature from the URL params that define filters/pagination/date/timeframe
            const queryParams = {};
            const q = this.$route.query || {};
            if (q.date) queryParams.date = q.date;
            if (q.timeframe) queryParams.timeframe = q.timeframe;
            if (q.category) queryParams.category = q.category;
            if (q.tag) queryParams.tag = q.tag;
            if (q.page) queryParams.page = q.page;
            return buildFilterSignatureFromQuery(queryParams);
        },
        rememberLastVisited(contentId) {
            try {
                const signature = this.getCurrentFilterSignature();
                saveLastVisitedItem(this.$route.path, signature, contentId);
            } catch (e) { /* no-op */ }
        },
        enhanceContentAttribsWithCategory() {
            for (var k = 0; k < this.dataSet.data.length; k++) {
                var content = this.dataSet.data[k];
                /*get the categories from this content*/
                var cats = getFieldValues(content, 'kultur_kategorien');
                /*check if there is a value in the cats*/
                if (cats != null && cats.length > 0) {
                    /*is cats an array?*/
                    if (Array.isArray(cats)) {
                        content.category = cats[0];
                    }
                    else {
                        content.category = cats;
                    }
                }
            }
        },
        sortContentsByCategories() {
            return sortByProperty(this.dataSet.data, 'category', 'de');
        },
        updateUrl() {
            const queryParams = {};

            // Add selected date - format as YYYY-MM-DD to avoid timezone issues
            if (this.dataSet.query.selectedDate) {
                let dateToFormat;
                if (typeof this.dataSet.query.selectedDate === 'string') {
                    // Already a string in YYYY-MM-DD format
                    dateToFormat = this.dataSet.query.selectedDate;
                } else {
                    // Date object - extract local date components
                    const date = new Date(this.dataSet.query.selectedDate);
                    const year = date.getFullYear();
                    const month = String(date.getMonth() + 1).padStart(2, '0');
                    const day = String(date.getDate()).padStart(2, '0');
                    dateToFormat = `${year}-${month}-${day}`;
                }
                queryParams.date = dateToFormat;
            }

            // Add selected time frame
            if (this.dataSet.query.selectedTimeFrame && this.dataSet.query.selectedTimeFrame !== null) {
                queryParams.timeframe = encodeURIComponent(this.dataSet.query.selectedTimeFrame);
            }

            // Add individual filter values to URL
            if (this.dataSet.query.selectedCategory && this.dataSet.query.selectedCategory !== null) {
                queryParams.category = encodeURIComponent(this.dataSet.query.selectedCategory);
            }

            if (this.dataSet.query.selectedTag && this.dataSet.query.selectedTag !== null) {
                queryParams.tag = encodeURIComponent(this.dataSet.query.selectedTag);
            }

            // Add page if not first page
            if (this.dataSet.query.page > 1) {
                queryParams.page = this.dataSet.query.page;
            }

            this.$router.replace({
                query: {
                    ...queryParams
                }
            });
        },
        initializeFromUrl() {
            const query = this.$route.query;

            // Initialize selected date from URL - parse as local date
            if (query.date) {
                try {
                    // Parse date string (YYYY-MM-DD) as local date
                    const dateParts = query.date.split('-');
                    if (dateParts.length === 3) {
                        const year = parseInt(dateParts[0], 10);
                        const month = parseInt(dateParts[1], 10) - 1; // Month is 0-indexed
                        const day = parseInt(dateParts[2], 10);
                        this.dataSet.query.selectedDate = new Date(year, month, day);
                    } else {
                        // Fallback to parsing as Date object
                        const parsedDate = new Date(query.date);
                        const year = parsedDate.getFullYear();
                        const month = parsedDate.getMonth();
                        const day = parsedDate.getDate();
                        this.dataSet.query.selectedDate = new Date(year, month, day);
                    }
                } catch (error) {
                    console.error('Error parsing date from URL:', error);
                    // Default to today as local date
                    const today = new Date();
                    this.dataSet.query.selectedDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
                }
            }

            // Initialize time frame from URL
            if (query.timeframe) {
                this.dataSet.query.selectedTimeFrame = decodeURIComponent(query.timeframe);
            }

            // Initialize page from URL
            if (query.page) {
                const page = parseInt(query.page) || 1;
                this.dataSet.query.page = page;
            }

            // Convert URL params to Search component format and process through original logic
            const searchQuery = {};
            if (query.category) {
                searchQuery.selectedCategory = decodeURIComponent(query.category);
            }
            if (query.tag) {
                searchQuery.selectedTag = decodeURIComponent(query.tag);
            }

            // If we have filters from URL, process them through the original logic
            if (Object.keys(searchQuery).length > 0) {
                this.searchParamsHaveChanged(searchQuery);
            } else {
                this.getContents();
            }
        },
        resetAllFilters() {
            this.dataSet.query.selectedDate = new Date();
            this.dataSet.query.selectedTimeFrame = null;
            this.dataSet.query.keyword = [];
            this.dataSet.query.selectedCategory = null;
            this.dataSet.query.selectedTag = null;
            this.dataSet.query.page = 1;

            // Clear URL parameters
            this.$router.replace({
                query: {}
            });

            this.getContents();
        },
        hasActiveFilters() {
            const today = new Date();
            const selectedDate = this.dataSet.query.selectedDate;
            const isDateToday = selectedDate &&
                selectedDate.toDateString() === today.toDateString();

            return !isDateToday ||
                this.dataSet.query.selectedTimeFrame ||
                this.dataSet.query.selectedCategory ||
                this.dataSet.query.selectedTag ||
                (this.dataSet.query.keyword && this.dataSet.query.keyword.length > 0);
        },
        dateTimeSearchParamsHaveChanged(params) {
            // Ensure selectedDate is a local Date object
            if (params.selectedDate) {
                const date = new Date(params.selectedDate);
                this.dataSet.query.selectedDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
            } else {
                const today = new Date();
                this.dataSet.query.selectedDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
            }
            this.dataSet.query.selectedTimeFrame = params.selectedTimeFrame;
            this.dataSet.query.page = 1;
            this.updateUrl();
            this.getContents();
        },
        searchParamsHaveChanged(query) {
            // ORIGINAL working logic - UNCHANGED
            this.dataSet.query.keyword = [];
            Object.keys(query).forEach((k) => {
                if (query[k] != null && query[k] !== '') {
                    this.dataSet.query.keyword.push(query[k]);
                }
            });

            // Store only for URL display (mirrors what was just processed above)
            this.dataSet.query.selectedCategory = query.selectedCategory || null;
            this.dataSet.query.selectedTag = query.selectedTag || null;

            this.dataSet.query.page = 1;
            this.updateUrl();
            this.getContents();
        }
    }
}
</script>

<style lang="scss" scoped>
@import '@/scss/_variables.scss';

.no-calendar-results {

    .container-fluid {
        max-width: 250px;
        margin: 0 auto;
        text-align: center;
    }

    .blank {
        display: flex;
        align-items: center;
        height: 60vh;
    }
}

.directory {
    background: #fff;

    h1 {
        font-size: 24px;
        margin: 30px 0;
    }

    &--element {
        position: relative;
        overflow: hidden;
        padding: 15px 0;

        .title {
            position: relative;
            background: #fff;
            display: inline-block;
            padding-right: 20px;

            &::after {
                content: '';
                position: absolute;
                left: 100%;
                bottom: 6px;
                width: 1500px;
                height: 3px;
                background: $secondary;
                z-index: 1;
            }
        }

        &__single {
            position: relative;
            margin-top: 30px;

            @media (max-width: 786px) {
                margin-top: 20px;
            }
        }

        margin-bottom: 50px;
    }

    &.footer-margin {
        @media (max-width: 786px) {
            margin-bottom: 0 !important;
        }
    }
}
</style>
